import { apiClient } from './client';

/**
 * 부서원 인사정보 — **팀장 화면** (시안 B10).
 *
 * 서버: `apps/chat-server/src/modules/hr/team-hr.routes.ts`.
 *
 * ★★ **담당자 화면(A3)보다 좁다.** 팀장이 보는 것은 «일을 같이 하는 데 필요한 것» 까지다 —
 *    주민등록번호 · 계좌 · 주소 · 임금은 오지 않고, **개인 휴대전화와 생일도 없다**.
 * ★ 팀장은 자기 팀만, 회사 관리자는 전사. 그 밖에는 403 — 부르는 쪽이 화면을 접는다.
 * ★ 조회는 **감사에 남는다**(남의 인사정보를 열어 본 일이다).
 */

export interface TeamHrTeamDto {
  id: string;
  name: string;
  divisionName: string | null;
  isLeader: boolean;
}

export interface TeamHrMemberDto {
  userId: string;
  name: string;
  position: string | null;
  avatarUrl: string | null;
  isLeader: boolean;
  resigned: boolean;
}

export interface TeamHrListDto {
  teams: TeamHrTeamDto[];
  team: TeamHrTeamDto;
  members: TeamHrMemberDto[];
}

export interface TeamHrPersonDto {
  userId: string;
  name: string;
  nameEn: string | null;
  email: string;
  position: string | null;
  jobLevel: number | null;
  employeeNumber: string | null;
  hireDate: string | null;
  extension: string | null;
  deskLocation: string | null;
  employmentStatus: string | null;
  employmentType: string | null;
  avatarUrl: string | null;
  isLeader: boolean;
  teamName: string;
  divisionName: string | null;
  scheduleName: string | null;
  baseStart: string;
  baseEnd: string;
}

export interface TeamHrDetailDto {
  year: number;
  month: number;
  person: TeamHrPersonDto;
  attendance: {
    creditedMinutes: number;
    overtimeMinutes: number;
    workedDays: number;
    leaveDays: number;
    needsCloseDays: number;
    late: number;
  };
  /** ★ `annualTotal` 이 null 이면 «아직 산정되지 않았다» — 0 이 아니다. */
  leave: { annualTotal: number | null; usedDays: number; remainDays: number | null };
  pendingRequests: { leave: number; overtime: number; total: number };
  tzOffsetMinutes: number;
}

export async function fetchTeamHr(teamId?: string): Promise<TeamHrListDto> {
  const { data } = await apiClient.get<TeamHrListDto>('/hr/team-hr', {
    params: teamId ? { teamId } : undefined,
  });
  return { ...data, members: Array.isArray(data?.members) ? data.members : [] };
}

export async function fetchTeamHrPerson(
  userId: string, year?: number, month?: number,
): Promise<TeamHrDetailDto> {
  const { data } = await apiClient.get<TeamHrDetailDto>(`/hr/team-hr/${userId}`, {
    params: year && month ? { year, month } : undefined,
  });
  return data;
}

/** 분을 «134:20» 으로 — 근무 시간은 시:분이 관례다(2.2h 보다 읽기 쉽다). */
export function hourMinText(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/** 입사일로 근속을 «7년 5개월» 로. 입사일이 없으면 null. */
export function tenureText(hireDate: string | null, now = new Date()): string | null {
  if (!hireDate || hireDate.length < 10) return null;
  const y = Number(hireDate.slice(0, 4));
  const m = Number(hireDate.slice(5, 7));
  const d = Number(hireDate.slice(8, 10));
  if (!Number.isInteger(y) || !Number.isInteger(m)) return null;
  let months = (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m);
  if (now.getDate() < d) months -= 1;
  if (months < 0) return null;
  const yy = Math.floor(months / 12);
  const mm = months % 12;
  if (yy === 0) return `${mm}개월`;
  return mm === 0 ? `${yy}년` : `${yy}년 ${mm}개월`;
}
